import { useCallback, useEffect, useRef, useState } from 'react';

import type { FeedbackTag } from '@sb/feathers-types';

import type { FeedbackSentiment, SubmittedFeedback } from './MessageFeedback';

/**
 * One persisted `agentFeedback` row, reduced to what the thumbs row
 * needs to re-render itself. The service enforces a UNIQUE constraint
 * on `messageID`, so at most one of these exists per rated message.
 */
export interface PersistedFeedback {
  messageID: string;
  sentiment: FeedbackSentiment;
  reason?: string;
  tags?: FeedbackTag[];
  quote?: string;
}

export interface SessionFeedbackOperations {
  /**
   * Loads every feedback row for a session. Implementations call
   * feathers `agentFeedback.find({ query: { sessionID } })`.
   */
  list: (sessionID: string) => Promise<PersistedFeedback[]>;
}

export interface UseSessionFeedbackOptions {
  /** Session whose feedback to load. Null clears the map. */
  sessionID: string | null;
  /** Injected feathers operations; the production set is wired in
   * by the chat mount. */
  operations: SessionFeedbackOperations;
  /** Fires when the load fails. Loading feedback is best-effort. */
  onError?: (error: Error) => void;
}

export interface UseSessionFeedbackResult {
  /** Persisted (and locally recorded) feedback, keyed by message id. */
  feedbackByMessageID: ReadonlyMap<string, SubmittedFeedback>;
  /**
   * Merges a verdict whose row the caller has confirmed exists into the
   * map, so the thumbs keep their state even if the message list re-seeds
   * and remounts the `MessageFeedback` instances before the next fetch.
   * Callers record after the write lands, never before: an entry here
   * shadows the server rows, so a verdict that failed to persist would
   * mask the real row for the rest of the session.
   */
  recordSubmission: (messageID: string, feedback: SubmittedFeedback) => void;
}

const EMPTY_FEEDBACK: ReadonlyMap<string, SubmittedFeedback> = new Map();

/**
 * Rehydrates thumbs feedback for the loaded session.
 *
 * Feedback has always been persisted (`agentFeedback` rows), but
 * `MessageFeedback` only ever knew about verdicts submitted in the
 * current mount — so leaving a chat and coming back showed every
 * message as un-rated. This hook fetches the session's rows once per
 * session and hands them back keyed by message id.
 *
 * Failures are swallowed into `onError`: feedback is a side channel,
 * and a fetch failure must not stop the transcript from rendering.
 */
export function useSessionFeedback(
  options: UseSessionFeedbackOptions,
): UseSessionFeedbackResult {
  const { sessionID, operations, onError } = options;

  const operationsRef = useRef(operations);
  const onErrorRef = useRef(onError);

  operationsRef.current = operations;
  onErrorRef.current = onError;

  const [feedbackByMessageID, setFeedbackByMessageID] =
    useState<ReadonlyMap<string, SubmittedFeedback>>(EMPTY_FEEDBACK);

  // Guards against a stale in-flight load for a previous session
  // overwriting the current one's map.
  const loadTokenRef = useRef(0);

  useEffect(() => {
    loadTokenRef.current += 1;

    const myToken = loadTokenRef.current;

    setFeedbackByMessageID(EMPTY_FEEDBACK);

    if (sessionID === null) return undefined;

    operationsRef.current
      .list(sessionID)
      .then((rows) => {
        if (myToken !== loadTokenRef.current) return;

        const next = new Map<string, SubmittedFeedback>();

        for (const row of rows) {
          next.set(row.messageID, {
            sentiment: row.sentiment,
            reason: row.reason,
            tags: row.tags,
            quote: row.quote,
          });
        }

        // A verdict recorded while the load was in flight is newer than
        // anything the server returned, so it wins.
        setFeedbackByMessageID((current) => {
          if (current.size === 0) return next;

          for (const [messageID, feedback] of current) {
            next.set(messageID, feedback);
          }

          return next;
        });
      })
      .catch((err: unknown) => {
        if (myToken !== loadTokenRef.current) return;

        onErrorRef.current?.(
          err instanceof Error ? err : new Error(String(err)),
        );
      });

    return undefined;
  }, [sessionID]);

  const recordSubmission = useCallback(
    (messageID: string, feedback: SubmittedFeedback) => {
      setFeedbackByMessageID((current) => {
        const next = new Map(current);

        next.set(messageID, feedback);

        return next;
      });
    },
    [],
  );

  return { feedbackByMessageID, recordSubmission };
}
